export interface StatItem {
  value: string;
  label: string;
}

export interface NavSubPage {
  slug: string;
  path: string;
  label: string;
  icon: string;
}

/** Sub-pages shown in the "What DAP can do?" dropdown. */
export const dapSubPages: NavSubPage[] = [
  { slug: 'overview', path: '/portal', label: 'Overview', icon: '🎯' },
  { slug: 'data-intelligence', path: '/portal/data-intelligence', label: 'Data Intelligence', icon: '📊' },
  { slug: 'automation', path: '/portal/automation', label: 'Automation', icon: '⚙️' },
  { slug: 'sqm-expert', path: '/portal/sqm-expert', label: 'SQM Expert', icon: '🧠' },
  { slug: 'sqm-consultant', path: '/portal/sqm-consultant', label: 'Readiness Review', icon: '🧭' },
  { slug: 'supplier-process-control', path: '/portal/supplier-process-control', label: 'Remote Supplier Process Control', icon: '🏭' },
  { slug: 'deep-research', path: '/portal/deep-research', label: 'Deep Research', icon: '🔬' },
];

export interface NavLinkItem {
  path: string;
  label: string;
  icon: string;
}

/** Top-level nav items shown after the dropdown. */
export const navLinks: NavLinkItem[] = [
  { path: '/portal/sqm-data-hub', label: 'SQM Data Hub', icon: '🗄️' },
  { path: '/portal/contributions', label: 'Contributions', icon: '🏆' },
];

export interface InfoPageContent {
  title: string;
  icon: string;
  intro: string;
  points: string[];
}

export interface OverviewContent {
  about: {
    title: string;
    paragraphs: string[];
  };
  mission: {
    title: string;
    items: string[];
  };
}

export interface PillarItem {
  key: string;
  title: string;
  percent: number;
  color: 'green' | 'orange' | 'purple';
  description: string;
  caption: string;
}

export interface TenetItem {
  key: string;
  icon: string;
  title: string;
  description: string;
}

export interface ModuleItem {
  title: string;
  description: string;
  icon: string;
}

export interface PlatformContribution {
  title: string;
  badge: string;
  description: string;
}

export interface TechComponent {
  title: string;
  description: string;
}

export interface DecisionStep {
  step: number;
  label: string;
}

export interface ContributionsContent {
  platform: {
    title: string;
    icon: string;
    items: PlatformContribution[];
  };
  components: {
    title: string;
    icon: string;
    items: TechComponent[];
  };
  framework: {
    title: string;
    icon: string;
    steps: DecisionStep[];
  };
}

/** Shared hero block shape used across the feature pages. */
export interface FeatureHero {
  title: string;
  badge: string;
  subtitle: string;
  percent: number;
  description: string;
}

export interface DataIntelligenceContent {
  hero: FeatureHero;
  highlights: string[];
  howItWorks: {
    before: string;
    after: string;
  };
  examples: string[];
  launchUrl: string;
}

export interface AutomationExample {
  icon: string;
  title: string;
  rows: { label: string; text: string }[];
  saving: string;
}

export interface AutomationContent {
  hero: FeatureHero;
  highlights: string[];
  examples: AutomationExample[];
}

export interface QuickApp {
  title: string;
  description: string;
  linkLabel: string;
  url: string;
}

export interface BenefitItem {
  icon: string;
  title: string;
  description: string;
}

export interface SqmExpertContent {
  hero: FeatureHero;
  benefits: BenefitItem[];
  launchUrl: string;
}

export interface DevProgressCard {
  status: 'production' | 'active';
  statusLabel: string;
  title: string;
  items: string[];
}

export interface AgentExample {
  title: string;
  bullets: string[];
  media: string;
  mediaAlt: string;
}

export interface SqmConsultantContent {
  hero: FeatureHero;
  highlights: string[];
  devProgress: DevProgressCard[];
  realExample: {
    specificAgent: AgentExample;
    agentCore: AgentExample;
  };
}

export interface ComparisonSide {
  title: string;
  description: string;
}

export interface ProcessControlContent {
  hero: FeatureHero;
  highlights: string[];
  comparison: {
    before: ComparisonSide;
    after: ComparisonSide;
  };
  launchUrl: string;
}

/**
 * Aggregate shape of the sensitive portal content fetched at runtime from the
 * Midway-protected `/api/content` endpoint. This data is intentionally NOT
 * bundled into the static JS — see `ContentContext.tsx`.
 */
export interface PortalContent {
  heroStats: StatItem[];
  pillars: PillarItem[];
  tenets: TenetItem[];
  overviewContent: OverviewContent;
  modules: ModuleItem[];
  infoPages: Record<string, InfoPageContent>;
  contributionsContent: ContributionsContent;
  dataIntelligenceContent: DataIntelligenceContent;
  automationContent: AutomationContent;
  automationQuickApps: QuickApp[];
  sqmExpertContent: SqmExpertContent;
  sqmConsultantContent: SqmConsultantContent;
  processControlContent: ProcessControlContent;
}
